import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@env/environment';
import { API_BASE_URL } from 'src/swagger/api.client.generated';

/**
 * Primes the browser's HTTP cache (and NodeBB's own session) for the Community
 * forum well before the user ever opens it, so CommunityComponent's real iframe
 * mostly hits cache instead of a cold network load on first visit - that cold
 * load (the forum SPA's own JS/CSS downloading and booting inside the iframe)
 * is the dominant cost in "Community feels slow to open", and it's NodeBB's own
 * bundle, not something `CommunityRouteReuseStrategy` (which only avoids
 * *repeat* loads within a session) can help with.
 *
 * This is a cache-warming trick, not DOM/state reuse: the hidden iframe used
 * here is thrown away once it settles. `CommunityComponent` still mounts its
 * own iframe when the user actually opens the page - by then its requests for
 * the same static assets should mostly resolve from the browser's HTTP cache
 * instead of network, and its `nodebb_token` cookie is already the one this
 * warm-up minted (not a fresh anonymous session).
 *
 * Uses the same hidden-iframe technique as `CircleEmbedComponent`'s SSO
 * bootstrap step, but as a plain DOM element (not an Angular template) since
 * nothing here needs change detection or to be visible in a component tree.
 *
 * Also doubles as the mechanism for pushing profile changes to the forum: NodeBB's
 * session-sharing plugin only applies a cookie's JWT payload (email, username) when
 * it verifies a fresh cookie against a real request to its origin - a re-minted
 * cookie sitting unused in the browser doesn't reach NodeBB until the user happens
 * to open Community next. `resync()` re-runs the same POST-then-hidden-iframe
 * sequence on demand (unlike `warm()`, not gated to once per session) so Settings
 * can call it right after saving a new email/preferred name and have NodeBB pick
 * up the change immediately instead of lazily.
 */
@Injectable({ providedIn: 'root' })
export class CommunityWarmupService {
  private static readonly FAILSAFE_MS = 15000;

  private started = false;

  constructor(
    private http: HttpClient,
    @Inject(API_BASE_URL) private apiBaseUrl: string
  ) {}

  /** Idempotent - safe to call repeatedly (e.g. on every navigation); only the first call does anything. */
  warm(): void {
    if (this.started) {
      return;
    }
    this.started = true;
    this.runWarmup();
  }

  /**
   * Unlike `warm()`, not gated to once per session - call after a profile change
   * (email, preferred name) that re-minted the nodebb_token cookie, so the new
   * value reaches NodeBB right away instead of waiting for the user's next
   * Community visit.
   */
  resync(): void {
    this.runWarmup();
  }

  private runWarmup(): void {
    // Same session-establishment call CommunityComponent itself makes on open - the
    // nodebb_token cookie has to exist before the hidden iframe navigates, or NodeBB's
    // session-sharing plugin sees a credential-less request and this primes an anonymous
    // guest page instead of the user's actual session.
    this.http
      .post(`${this.apiBaseUrl}/api/Account/NodeBBSession`, null)
      .subscribe({
        next: () => this.loadHiddenFrame(),
        error: () => this.loadHiddenFrame(),
      });
  }

  private loadHiddenFrame(): void {
    const frame = document.createElement('iframe');
    frame.src = environment.communityForumUrl;
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('tabindex', '-1');
    frame.style.position = 'fixed';
    frame.style.top = '0';
    frame.style.left = '0';
    frame.style.width = '1px';
    frame.style.height = '1px';
    frame.style.opacity = '0';
    frame.style.pointerEvents = 'none';
    frame.style.border = '0';

    const cleanup = () => frame.remove();
    // Give up on the warm-up (but keep whatever's already cached) rather than leaking
    // the iframe forever if `load` never fires (blocked frame, network stall).
    const failsafe = window.setTimeout(cleanup, CommunityWarmupService.FAILSAFE_MS);
    frame.addEventListener('load', () => {
      window.clearTimeout(failsafe);
      cleanup();
    });

    document.body.appendChild(frame);
  }
}
